/**
 * @file care-mcp-token.schema.ts
 * @description Token pessoal do conector MCP de jornadas (`mcpTokens`) —
 * plano "Conector MCP de Jornadas", fase M1.
 *
 * É a credencial provisória até o OAuth (M2): uma pessoa interna emite no
 * admin, cola na própria IA, e a IA passa a falar com o conector em nome
 * dela, NUMA marca. Só o hash do token é guardado — o valor aparece uma vez,
 * na emissão, e não há como recuperá-lo depois.
 *
 * O documento tem id = hash (SHA-256 em hex): a validação a cada chamada é
 * uma leitura direta, sem consulta.
 */

import { z } from 'zod';

export const ESCOPOS_DO_CONECTOR = ['jornadas:ler', 'jornadas:propor'] as const;
export const EscopoDoConectorEnum = z.enum(ESCOPOS_DO_CONECTOR);
export type EscopoDoConector = z.infer<typeof EscopoDoConectorEnum>;

/** Prefixo fixo: identifica o token num log ou num vazamento sem revelar o resto. */
export const PREFIXO_DO_TOKEN_MCP = 'eva_mcp_';
/** Token de acesso emitido pelo OAuth (M2) — vale 1 hora. */
export const PREFIXO_DO_TOKEN_DE_ACESSO = 'eva_oat_';
/** Token de renovação do OAuth — troca-se por um par novo, uma vez só. */
export const PREFIXO_DO_TOKEN_DE_RENOVACAO = 'eva_ort_';

/**
 * PESSOAL: emitido à mão no admin (M1). ACESSO e RENOVACAO: emitidos pelo
 * OAuth (M2). Só PESSOAL e ACESSO abrem o conector; RENOVACAO só serve no
 * endpoint de token.
 */
export const TipoDeTokenMcpEnum = z.enum(['PESSOAL', 'ACESSO', 'RENOVACAO']);
export type TipoDeTokenMcp = z.infer<typeof TipoDeTokenMcpEnum>;

export const McpTokenSchema = z.object({
  /** Id público (para listar e revogar). Não é o token. */
  id: z.string().uuid(),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  /** Primeiros caracteres, para a pessoa reconhecer o token na lista. */
  prefixo: z.string(),
  brandId: z.string(),
  /** Quem emitiu e em nome de quem a IA age. */
  emitidoPara: z.string(),
  /** Nome da IA onde o token será usado ("Claude Desktop") — vai para o selo. */
  cliente: z.string().min(1).max(80),
  escopos: z.array(EscopoDoConectorEnum).min(1),
  criadoEm: z.date(),
  expiraEm: z.date(),
  revogadoEm: z.date().nullable().default(null),
  revogadoPor: z.string().nullable().default(null),
  ultimoUsoEm: z.date().nullable().default(null),
  // --- OAuth (M2). Opcionais: os tokens pessoais da M1 não têm. ---
  tipo: TipoDeTokenMcpEnum.default('PESSOAL'),
  /** client_id OAuth da IA. */
  clienteId: z.string().nullable().default(null),
  /** URL canônica do conector para a qual o token foi emitido (RFC 8707). */
  audiencia: z.string().nullable().default(null),
  /** Uma autorização gera uma família (acesso + renovações). Revogar a família derruba tudo. */
  familia: z.string().nullable().default(null),
  /** Renovação já trocada: reaparecer é sinal de roubo, e derruba a família. */
  trocadoEm: z.date().nullable().default(null),
});
export type McpToken = z.infer<typeof McpTokenSchema>;
